// ============================================================
// Braval - Auctions Page
// ============================================================

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../api';
import { Header } from '../App';

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

// ------------------------------------------------------------
// Filters Sidebar
// ------------------------------------------------------------
function AuctionFilters({ filters, setFilters, onSearch }) {
    const handleChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClear = () => {
        setFilters({ projectName: '', auctioneer: '', dataSource: '', yearFrom: '', yearTo: '', minLots: '10', maxLots: '' });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') onSearch();
    };

    return (
        <div>
            <div className="filters-title">Filters</div>

            <div className="filter-group">
                <label className="filter-label">Project Name</label>
                <input
                    type="text"
                    className="filter-input"
                    placeholder="e.g. Jabil"
                    value={filters.projectName}
                    onChange={(e) => handleChange('projectName', e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>

            <div className="filter-group">
                <label className="filter-label">Auctioneer</label>
                <input
                    type="text"
                    className="filter-input"
                    placeholder="e.g. TBG"
                    value={filters.auctioneer}
                    onChange={(e) => handleChange('auctioneer', e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>

            <div className="filter-group">
                <label className="filter-label">Data Source</label>
                <select
                    className="filter-input"
                    value={filters.dataSource}
                    onChange={(e) => handleChange('dataSource', e.target.value)}
                >
                    <option value="">All</option>
                    <option value="Comps-Classic">Comps-Classic</option>
                    <option value="Observed">Observed</option>
                    <option value="TBG Bidspotter">TBG Bidspotter</option>
                    <option value="TBG Classic">TBG Classic</option>
                </select>
            </div>

            <div className="filter-group">
                <label className="filter-label">Auction Year</label>
                <div className="filter-range">
                    <input
                        type="number"
                        className="filter-input filter-input-half"
                        placeholder="From"
                        value={filters.yearFrom}
                        onChange={(e) => handleChange('yearFrom', e.target.value)}
                        onKeyDown={handleKeyDown}
                        min="1900"
                        max="2100"
                    />
                    <span className="filter-range-sep">–</span>
                    <input
                        type="number"
                        className="filter-input filter-input-half"
                        placeholder="To"
                        value={filters.yearTo}
                        onChange={(e) => handleChange('yearTo', e.target.value)}
                        onKeyDown={handleKeyDown}
                        min="1900"
                        max="2100"
                    />
                </div>
            </div>

            <div className="filter-group">
                <label className="filter-label">Number of Lots</label>
                <div className="filter-range">
                    <input
                        type="number"
                        className="filter-input filter-input-half"
                        placeholder="Low"
                        value={filters.minLots}
                        onChange={(e) => handleChange('minLots', e.target.value)}
                        onKeyDown={handleKeyDown}
                        min="0"
                    />
                    <span className="filter-range-sep">–</span>
                    <input
                        type="number"
                        className="filter-input filter-input-half"
                        placeholder="High"
                        value={filters.maxLots}
                        onChange={(e) => handleChange('maxLots', e.target.value)}
                        onKeyDown={handleKeyDown}
                        min="0"
                    />
                </div>
            </div>

            <button className="filter-search-button" onClick={onSearch}>Search</button>
            <button className="filter-clear-button" onClick={handleClear}>Clear Filters</button>
        </div>
    );
}

// ------------------------------------------------------------
// Results Table
// ------------------------------------------------------------

const COLUMNS = [
    { key: 'project_name',   label: 'Project Name',  defaultDir: 'asc'  },
    { key: 'auction_date',   label: 'Auction Date',  defaultDir: 'desc' },
    { key: 'auctioneer_firm',label: 'Auctioneer',    defaultDir: 'asc'  },
    { key: 'location_region',label: 'Location',      defaultDir: 'asc'  },
    { key: 'data_source',    label: 'Data Source',   defaultDir: 'asc'  },
    { key: 'lot_count',      label: 'Lots',          defaultDir: 'desc', numeric: true },
    { key: 'total_value',    label: 'Total Value',   defaultDir: 'desc', numeric: true },
];

function sortAuctions(auctions, sortKey, sortDir) {
    if (!sortKey) return auctions;
    return [...auctions].sort((a, b) => {
        let av = a[sortKey], bv = b[sortKey];
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
    });
}

function AuctionsTable({ auctions, loading, hasSearched, error }) {
    const [sortKey, setSortKey] = useState('auction_date');
    const [sortDir, setSortDir] = useState('desc');
    const navigate = useNavigate();

    const handleSort = (col) => {
        if (sortKey === col.key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(col.key);
            setSortDir(col.defaultDir);
        }
    };

    if (loading) {
        return (
            <div className="results-status">
                <span className="loading-spinner"></span>
                Loading...
            </div>
        );
    }

    if (error) {
        return <div className="results-status results-error">{error}</div>;
    }

    if (!hasSearched) {
        return <div className="results-status">Loading auctions...</div>;
    }

    if (auctions.length === 0) {
        return <div className="results-status">No auctions found. Try adjusting your filters.</div>;
    }

    const sorted = sortAuctions(auctions, sortKey, sortDir);

    return (
        <>
            <div className="results-count">
                {auctions.length} auction{auctions.length !== 1 ? 's' : ''} found
            </div>
            <table className="results-table">
                <thead>
                    <tr>
                        {COLUMNS.map(col => (
                            <th
                                key={col.key}
                                className={`sortable-th${col.numeric ? ' col-price' : ''}`}
                                onClick={() => handleSort(col)}
                            >
                                {col.label}
                                <span className="sort-indicator">
                                    {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row) => (
                        <tr key={row.project_id} className="results-row-clickable" onClick={() => navigate(`/auctions/${row.project_id}`)}>
                            <td>{row.project_name}</td>
                            <td>{formatDate(row.auction_date)}</td>
                            <td>{row.auctioneer_firm || '—'}</td>
                            <td>{row.location_region || '—'}</td>
                            <td>{row.data_source || '—'}</td>
                            <td className="col-price">{row.lot_count}</td>
                            <td className="col-price sale-price">{formatCurrency(row.total_value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

// ------------------------------------------------------------
// Page
// ------------------------------------------------------------
export default function AuctionsPage() {
    const [filters, setFilters] = useState({
        projectName: '',
        auctioneer:  '',
        dataSource:  '',
        yearFrom:    '',
        yearTo:      '',
        minLots:     '10',
        maxLots:     '',
    });
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState(null);

    const fetchAuctions = async (activeFilters) => {
        setLoading(true);
        setError(null);
        setHasSearched(true);
        try {
            const params = new URLSearchParams();
            if (activeFilters.projectName) params.append('projectName', activeFilters.projectName);
            if (activeFilters.auctioneer)  params.append('auctioneer',  activeFilters.auctioneer);
            if (activeFilters.dataSource)  params.append('dataSource',  activeFilters.dataSource);
            if (activeFilters.yearFrom)    params.append('yearFrom',    activeFilters.yearFrom);
            if (activeFilters.yearTo)      params.append('yearTo',      activeFilters.yearTo);
            if (activeFilters.minLots)     params.append('minLots',     activeFilters.minLots);
            if (activeFilters.maxLots)     params.append('maxLots',     activeFilters.maxLots);

            const res = await apiFetch(`/api/auctions?${params.toString()}`);
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'An error occurred.');
                setAuctions([]);
            } else {
                setAuctions(data);
            }
        } catch (err) {
            setError('Could not reach the API.');
            setAuctions([]);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchAuctions(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => fetchAuctions(filters);

    return (
        <div className="app-layout">
            <Header />
            <div className="app-body">
                <aside className="sidebar">
                    <AuctionFilters
                        filters={filters}
                        setFilters={setFilters}
                        onSearch={handleSearch}
                    />
                </aside>
                <main className="main-content">
                    <div className="search-bar">
                        <Link to="/auctions/new" className="search-button new-lot-button">+ New Auction</Link>
                    </div>
                    <AuctionsTable
                        auctions={auctions}
                        loading={loading}
                        hasSearched={hasSearched}
                        error={error}
                    />
                </main>
            </div>
        </div>
    );
}
